import React, { useMemo } from 'react';
import { Plus, Eye, Edit, Trash2 } from 'lucide-react';
import ModuleRow from './ModuleRow';
import type { PermissionsTableProps, ModulePermissions } from '../../../../../types/groupModulePermissions';

const PermissionsTable: React.FC<PermissionsTableProps> = ({
  modules,
  onPermissionChange,
  onBulkPermissionChange
}) => {
  // Calculate if all modules have a specific permission enabled
  const allPermissionsState = useMemo(() => {
    const permissions: Record<keyof ModulePermissions, boolean> = {
      create: modules.every(module => module.permissions.create),
      read: modules.every(module => module.permissions.read),
      update: modules.every(module => module.permissions.update),
      delete: modules.every(module => module.permissions.delete)
    };
    return permissions;
  }, [modules]);

  const handleBulkToggle = (permission: keyof ModulePermissions) => {
    const newValue = !allPermissionsState[permission];
    onBulkPermissionChange(permission, newValue);
  };

  return (
    <div className="permissions-table-container">
      <table className="permissions-table">
        <thead className="permissions-table-header">
          <tr>
            <th className="module-details-column">MODULE DETAILS</th>
            <th className="permission-column">
              <div className="permission-header">
                <Plus size={16} className="permission-icon" />
                <span>CREATE</span>
                <input
                  type="checkbox"
                  checked={allPermissionsState.create}
                  onChange={() => handleBulkToggle('create')}
                  className="bulk-permission-checkbox"
                  title="Toggle all CREATE permissions"
                />
                <span className="bulk-label">ALL</span>
              </div>
            </th>
            <th className="permission-column">
              <div className="permission-header">
                <Eye size={16} className="permission-icon" />
                <span>READ</span>
                <input
                  type="checkbox"
                  checked={allPermissionsState.read}
                  onChange={() => handleBulkToggle('read')}
                  className="bulk-permission-checkbox"
                  title="Toggle all READ permissions"
                />
                <span className="bulk-label">ALL</span>
              </div>
            </th>
            <th className="permission-column">
              <div className="permission-header">
                <Edit size={16} className="permission-icon" />
                <span>UPDATE</span>
                <input
                  type="checkbox"
                  checked={allPermissionsState.update}
                  onChange={() => handleBulkToggle('update')}
                  className="bulk-permission-checkbox"
                  title="Toggle all UPDATE permissions"
                />
                <span className="bulk-label">ALL</span>
              </div>
            </th>
            <th className="permission-column">
              <div className="permission-header">
                <Trash2 size={16} className="permission-icon" />
                <span>DELETE</span>
                <input
                  type="checkbox"
                  checked={allPermissionsState.delete}
                  onChange={() => handleBulkToggle('delete')}
                  className="bulk-permission-checkbox"
                  title="Toggle all DELETE permissions"
                />
                <span className="bulk-label">ALL</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="permissions-table-body">
          {modules.map((module) => (
            <ModuleRow
              key={module.id}
              module={module}
              onPermissionChange={onPermissionChange}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PermissionsTable;